import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { VehicleResult } from '../../types';

interface Props {
  vehicles: VehicleResult[];
}

const DEPRECIATION_COLOR = '#3a6fd4';
const INTEREST_COLOR = '#f59e0b';
const OPERATING_COLOR = '#10b981';

function getNum(v: VehicleResult, section: string, key: string): number {
  const sec = v[section as keyof VehicleResult] as Record<string, unknown> | null;
  if (!sec) return 0;
  const val = sec[key];
  return typeof val === 'number' ? val : 0;
}

// Phase 25.1: convert from absolute stacked bar to 100% proportional. Each
// bar reaches 100% height; segments show cost share (%) rather than raw
// dollars. The tooltip still surfaces the underlying $ amounts so users can
// see both the share and the absolute spend on hover.
interface ChartDatum {
  name: string;
  Depreciation: number;
  Interest: number;
  Operating: number;
  // Raw dollar values carried through to the tooltip.
  _depreciation_usd: number;
  _interest_usd: number;
  _operating_usd: number;
  _total_usd: number;
}

function buildChartData(vehicles: VehicleResult[]): ChartDatum[] {
  return vehicles.map((v) => {
    const dep = getNum(v, 'depreciation', 'total_depreciation');
    const intr = getNum(v, 'financing', 'total_interest_to_sale');
    const op = getNum(v, 'operating', 'horizon_total');
    const total = dep + intr + op;
    const share = (part: number) => (total > 0 ? (part / total) * 100 : 0);
    return {
      name: v.label,
      Depreciation: Number(share(dep).toFixed(2)),
      Interest: Number(share(intr).toFixed(2)),
      Operating: Number(share(op).toFixed(2)),
      _depreciation_usd: Math.round(dep),
      _interest_usd: Math.round(intr),
      _operating_usd: Math.round(op),
      _total_usd: Math.round(total),
    };
  });
}

function fmtDollar(v: number) {
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtPct(v: number) {
  return v.toFixed(1) + '%';
}

export default function TCOBreakdownChart({ vehicles }: Props) {
  const data = buildChartData(vehicles);

  // Tooltip formatter: pull the matching raw-dollar field from the payload's
  // datum so the tooltip can show both share and absolute spend in one row.
  const tooltipFormatter = (
    value: number | string | undefined,
    name: string | undefined,
    item: { payload?: ChartDatum } | undefined,
  ): [string, string] => {
    if (value === undefined || name === undefined) return ['-', name ?? ''];
    const pct = typeof value === 'number' ? value : Number(value);
    const datum = item?.payload;
    const dollarKey = name === 'Depreciation'
      ? '_depreciation_usd'
      : name === 'Interest'
        ? '_interest_usd'
        : name === 'Operating'
          ? '_operating_usd'
          : null;
    const usd = datum && dollarKey ? datum[dollarKey] : undefined;
    if (typeof usd === 'number') {
      return [`${fmtPct(pct)} (${fmtDollar(usd)})`, name];
    }
    return [fmtPct(pct), name];
  };

  return (
    <ResponsiveContainer width="100%" height={600}>
      <BarChart data={data} margin={{ top: 8, right: 24, left: 16, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#1e2530" />
        <XAxis
          dataKey="name"
          stroke="#8a93a2"
          tick={{ fill: '#8a93a2', fontSize: 14 }}
        />
        <YAxis
          stroke="#8a93a2"
          tick={{ fill: '#8a93a2', fontSize: 14 }}
          tickFormatter={(v: number) => `${v}%`}
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          width={60}
          label={{
            value: '% of Total TCO',
            angle: -90,
            position: 'insideLeft',
            fill: '#8a93a2',
            fontSize: 13,
            style: { textAnchor: 'middle' },
          }}
        />
        <Tooltip
          contentStyle={{ background: '#111418', border: '1px solid #1e2530', borderRadius: 8 }}
          labelStyle={{ color: '#8a93a2', fontSize: 15, fontWeight: 600 }}
          formatter={tooltipFormatter}
          cursor={{ fill: 'rgba(148,163,184,0.06)' }}
        />
        <Legend wrapperStyle={{ fontSize: 15, color: '#8a93a2' }} />
        <Bar dataKey="Depreciation" stackId="tco" fill={DEPRECIATION_COLOR} radius={[0, 0, 0, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={DEPRECIATION_COLOR} fillOpacity={0.85} />
          ))}
        </Bar>
        <Bar dataKey="Interest" stackId="tco" fill={INTEREST_COLOR}>
          {data.map((_, i) => (
            <Cell key={i} fill={INTEREST_COLOR} fillOpacity={0.85} />
          ))}
        </Bar>
        <Bar dataKey="Operating" stackId="tco" fill={OPERATING_COLOR} radius={[4, 4, 0, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={OPERATING_COLOR} fillOpacity={0.85} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// Exported for unit-test access in apps/api tests don't apply here. The
// `_buildChartData_TEST_ONLY` re-export gives a pure pivot we can sanity
// check from any external harness if needed.
export const _buildChartData_TEST_ONLY = buildChartData;
